import React, { useEffect, useState } from "react";
import {
  Check,
  Shield,
  Loader,
  Tag,
  Phone,
  Copy,
  Users,
  Calendar,
  Zap,
  MessageSquare,
  Ticket,
} from "lucide-react";
import { useAuth } from "../authentication/AuthContext";
import { toast } from "react-toastify";
import api from "../../utils/api";

const SubscriptionPlans = () => {
  const [plans, setPlans] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showCouponSelector, setShowCouponSelector] = useState(false);
  const [profile, setProfile] = useState(null);

  const { subscription, user } = useAuth();
  const CONTACT_NUMBER = "8570996916";

  // 📦 Fetch plans + coupons
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [plansRes, couponsRes, profileRes] = await Promise.all([
          api.get(`plans/active`),
          api.get(`coupons/active`),
          api.get(`user/${user?.id}`),
        ]);

        setPlans(plansRes.data.data || []);
        setCoupons(couponsRes.data.data || []);
        setProfile(profileRes.data || null);
      } catch (err) {
        console.error("Error loading subscription data", err);
        toast.error("Failed to load subscription plans");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle contact button click
  const handleContactClick = (plan) => {
    setSelectedPlan(plan);
    setShowCouponSelector(true);
  };

  // Handle coupon selection
  const handleCouponSelect = (coupon) => {
    setSelectedCoupon(coupon);
    setShowCouponSelector(false);
    setShowContactModal(true);
  };

  // Clear selections
  const clearSelections = () => {
    setSelectedPlan(null);
    setSelectedCoupon(null);
    setShowCouponSelector(false);
    setShowContactModal(false);
  };

  // Copy contact number to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(CONTACT_NUMBER);
    toast.success("Contact number copied to clipboard!");
  };

  // Get WhatsApp URL with pre-filled message
  const getWhatsAppUrl = () => {
    if (!selectedPlan) return `https://wa.me/91${CONTACT_NUMBER}`;

    const mobile = profile?.mobile || "Not Available";
    const owner = profile?.owner || "Not Available";
  const email = profile?.email || "Not Available";
  const shopType = profile?.shop_type || "Not Available";

  let message = `Hello! I would like to subscribe.\n\n`;

    message += `👤 User Details:\n`;
    message += `📱 Owner Name: ${owner}\n`;
    message += `📱 Registered Mobile: ${mobile}\n`;
    message += `📧 Email: ${email}\n`;
    message += `🏪 Shop Name: ${shopType}\n\n`;

    message += `📦 Plan Details:\n`;
    message += `Plan: ${selectedPlan.title}\n`;
    message += `Duration: ${selectedPlan.duration_days} days\n`;

    if (selectedCoupon) {
      message += `Coupon Code: ${selectedCoupon.code}\n`;
      message += `Final Price: ₹${calculateDiscountedPrice()}\n`;
    } else {
      message += `Price: ₹${selectedPlan.amount}\n`;
    }

    message += `\nPlease contact me to proceed further.`;

    return `https://wa.me/91${CONTACT_NUMBER}?text=${encodeURIComponent(message)}`;
  };

  // Copy WhatsApp message to clipboard
  const copyWhatsAppMessage = () => {
    const url = getWhatsAppUrl();
    const message = decodeURIComponent(url.split("text=")[1] || "");
    navigator.clipboard.writeText(message);
    toast.success("Message copied to clipboard!");
  };

  // Calculate discounted price
  const calculateDiscountedPrice = () => {
    if (!selectedPlan || !selectedCoupon) return selectedPlan?.amount || 0;

    const price = selectedPlan.amount;
    const discount =
      selectedCoupon.discount_type === "percentage"
        ? (price * selectedCoupon.discount_value) / 100
        : selectedCoupon.discount_value;

    const finalPrice = Math.max(price - discount, 0);
    return finalPrice.toFixed(2);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading subscription plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* 🔖 ACTIVE COUPONS BANNER */}
        {coupons.length > 0 && (
          <div className="mb-8 bg-green-600 text-white rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Tag className="h-5 w-5" />
                <span className="font-medium">Active Offers</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {coupons.slice(0, 2).map((coupon) => (
                  <div
                    key={coupon._id}
                    className="bg-white text-green-700 px-3 py-1 rounded-full text-xs font-semibold"
                  >
                    {coupon.code}
                  </div>
                ))}
                {coupons.length > 2 && (
                  <span className="text-sm">+{coupons.length - 2} more</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 🔐 HEADER */}
        <div className="text-center mb-10">
          <Shield className="h-12 w-12 text-blue-600 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900">
            Subscription Plans
          </h1>
          <p className="text-gray-600 mt-2">
            Choose a plan that fits your business needs
          </p>

          {/* ⭐ ACTIVE PLAN BADGE */}
          {subscription && subscription?.isActive && (
            <div className="mt-6 inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full">
              <div className="h-2 w-2 bg-green-500 rounded-full"></div>
              <span className="font-medium">
                Active Plan: {subscription.title || "N/A"}
              </span>
            </div>
          )}
        </div>

        {/* 💳 PLANS GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan) => {
            const isActive =
              subscription &&
              subscription?.isActive &&
              subscription?.status !== "trial" &&
              subscription?.planId === plan._id;

            const features = Array.isArray(plan.highlighting_features)
              ? plan.highlighting_features
              : typeof plan.highlighting_features === "string"
                ? plan.highlighting_features.split(",")
                : [];

            const isPopular =
              plan.title?.toLowerCase().includes("premium") ||
              plan.title?.toLowerCase().includes("pro");

            return (
              <div
                key={plan._id}
                className={`bg-white rounded-xl border overflow-hidden ${
                  isActive ? "border-blue-500 border-2" : "border-gray-200"
                }`}
              >
                {/* Popular Badge */}
                {isPopular && !isActive && (
                  <div className="bg-blue-600 text-white text-xs font-bold px-3 py-1 absolute right-4 top-4 rounded-full">
                    POPULAR
                  </div>
                )}

                {/* Header */}
                <div className="bg-blue-600 text-white p-6 text-center">
                  <h2 className="text-xl font-bold">{plan.title}</h2>
                  <div className="mt-2">
                    <span className="text-3xl font-bold">₹{plan.amount}</span>
                    <span className="text-blue-200 ml-1">
                      /{plan.duration_days} days
                    </span>
                  </div>
                </div>

                {/* Body */}
                <div className="p-6">
                  <p className="text-gray-600 text-sm mb-4">
                    {plan.description}
                  </p>

                  {/* Features */}
                  {features.length > 0 && (
                    <div className="mb-6">
                      <div className="flex items-center gap-2 text-gray-700 font-medium mb-2">
                        <Zap className="h-4 w-4 text-blue-500" />
                        <span>Features:</span>
                      </div>
                      <ul className="space-y-2">
                        {features.slice(0, 3).map((feature, index) => (
                          <li
                            key={index}
                            className="flex items-start gap-2 text-sm"
                          >
                            <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">
                              {feature.trim()}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Plan Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-6 p-3 bg-gray-50 rounded-lg">
                    <div className="text-center">
                      <Calendar className="h-4 w-4 text-gray-500 mx-auto mb-1" />
                      <div className="text-sm text-gray-600">Duration</div>
                      <div className="font-medium">
                        {plan.duration_days} days
                      </div>
                    </div>
                    <div className="text-center">
                      <Users className="h-4 w-4 text-gray-500 mx-auto mb-1" />
                      <div className="text-sm text-gray-600">Price</div>
                      <div className="font-medium">₹{plan.amount}</div>
                    </div>
                  </div>

                  {/* Contact Button */}
                  <button
                    onClick={() => handleContactClick(plan)}
                    disabled={isActive}
                    className={`w-full py-3 rounded-lg font-medium ${
                      isActive
                        ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700 text-white"
                    }`}
                  >
                    {isActive ? "Current Plan" : "Contact Now"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* 📞 CONTACT INFO BAR */}
        <div className="mt-12 bg-white rounded-xl p-6 border border-gray-200">
          <div className="text-center">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Need Assistance?
            </h3>
            <p className="text-gray-600 mb-4">
              Contact our sales team for any questions
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`https://wa.me/91${CONTACT_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-medium flex items-center justify-center gap-2"
              >
                <MessageSquare className="h-5 w-5" />
                WhatsApp Us
              </a>
              <button
                onClick={copyToClipboard}
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium flex items-center justify-center gap-2"
              >
                <Copy className="h-5 w-5" />
                Copy Number
              </button>
            </div>
            <div className="mt-4 text-gray-700">
              <div className="inline-flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-lg">
                <Phone className="h-4 w-4 text-blue-600" />
                <span className="font-mono font-medium">{CONTACT_NUMBER}</span>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Available 9 AM - 9 PM, Monday to Saturday
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 🎫 COUPON SELECTOR MODAL */}
      {showCouponSelector && selectedPlan && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-sm w-full p-6">
            <div className="text-center mb-6">
              <Ticket className="h-8 w-8 text-blue-600 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Apply Coupon?
              </h3>
              <p className="text-gray-600 text-sm">
                Do you have a coupon code for {selectedPlan.title}?
              </p>
            </div>

            {/* Coupon Options */}
            <div className="space-y-3 max-h-48 overflow-y-auto mb-6">
              <button
                onClick={() => handleCouponSelect(null)}
                className="w-full p-4 border border-gray-300 rounded-lg hover:border-blue-500 text-left"
              >
                <div className="font-medium">No Coupon</div>
                <div className="text-sm text-gray-600">
                  Continue without coupon
                </div>
              </button>

              {coupons.map((coupon) => (
                <button
                  key={coupon._id}
                  onClick={() => handleCouponSelect(coupon)}
                  className="w-full p-4 border border-green-200 bg-green-50 rounded-lg hover:border-green-500 text-left"
                >
                  <div className="font-medium text-green-800">
                    {coupon.code}
                  </div>
                  <div className="text-sm text-green-600">
                    {coupon.discount_type === "percentage"
                      ? `${coupon.discount_value}% OFF`
                      : `₹${coupon.discount_value} OFF`}
                  </div>
                </button>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                onClick={clearSelections}
                className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleCouponSelect(null)}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg font-medium hover:bg-blue-700"
              >
                Skip
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 📱 CONTACT MODAL */}
      {showContactModal && selectedPlan && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-sm w-full p-6">
            <div className="text-center">
              <Phone className="h-8 w-8 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Contact for {selectedPlan.title}
              </h3>
              <p className="text-gray-600 text-sm mb-6">
                A WhatsApp message will open with your plan details
              </p>

              {/* Plan Summary */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">Plan:</span>
                  <span>{selectedPlan.title}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">Price:</span>
                  <div>
                    {selectedCoupon ? (
                      <>
                        <span className="text-green-600 font-bold">
                          ₹{calculateDiscountedPrice()}
                        </span>
                        <span className="text-gray-500 text-sm line-through ml-2">
                          ₹{selectedPlan.amount}
                        </span>
                      </>
                    ) : (
                      <span className="font-bold">₹{selectedPlan.amount}</span>
                    )}
                  </div>
                </div>
                {selectedCoupon && (
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Coupon:</span>
                    <span className="text-green-600 font-medium">
                      {selectedCoupon.code}
                    </span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <a
                  href={getWhatsAppUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-green-500 hover:bg-green-600 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2"
                >
                  <MessageSquare className="h-5 w-5" />
                  Open WhatsApp
                </a>

                <button
                  onClick={copyWhatsAppMessage}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2"
                >
                  <Copy className="h-5 w-5" />
                  Copy Message
                </button>

                <button
                  onClick={clearSelections}
                  className="w-full border border-gray-300 text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPlans;
